/**
 * Logging Context Helper
 *
 * Extracts traceability context from the claim store for consistent logging.
 * Provides eventNumber, contractNumber, currentScreen, sessionId and parentClientId
 * for all logs.
 */

import { useClaimStore } from '../store/claimStore';
import { getSessionId } from './sessionManager';

export interface LoggingContext {
  eventNumber: string | null;
  contractNumber: string | null;
  currentScreen: string | null;
  sessionId: string;

  /**
   * The parent container's identifier for the connected user (DEV-7699).
   *
   * sessionId answers "which visit", parentClientId answers "which user" — together
   * they attribute an action. Named with the `parent` prefix in Datadog on purpose:
   * the value belongs to the host application, not to the claim, and it is the only
   * thing this identifier is still used for.
   *
   * Null until the parent context handshake completes (and on the JWT-only dev paths,
   * where the parent supplies no application metadata).
   */
  parentClientId: string | null;
}

/**
 * Extracts logging context from the current application state.
 * Falls back gracefully if the store is unavailable or data is missing.
 *
 * @returns {LoggingContext} Traceability context for logging
 */
export function getLoggingContext(): LoggingContext {
  try {
    const state = useClaimStore.getState();

    // Extract contract number with fallback chain
    // Multiple paths to ensure we capture the contract number at different claim stages
    const contractData = state.stepData['01-01-CONTRAT'] || {};
    const contractNumber =
      contractData.contractDetails?.number ||
      contractData.contractDetails?.contractNumber ||
      contractData.selectedContract ||
      state.currentClaim?.contractNumber ||
      null;

    return {
      eventNumber: state.eventNumber || null,
      contractNumber,
      currentScreen: state.currentScreenCode || null,
      sessionId: getSessionId(),
      parentClientId: state.parentContext?.clientId || null,
    };
  } catch (error) {
    // Fail gracefully if store unavailable (e.g., during initialization)
    return {
      eventNumber: null,
      contractNumber: null,
      currentScreen: null,
      sessionId: getSessionId(),
      parentClientId: null,
    };
  }
}
